import React from 'react';
import { useSwipeToDismiss } from '../hooks/useSwipeToDismiss';

interface Notification {
  id: string;
  notificationId: string;
  read: boolean;
  readAt?: string;
  createdAt: string;
  title: string;
  body: string;
  deliveredAt?: string;
  dismissed?: boolean;
  dismissedAt?: string;
}

export interface SwipeableNotificationCardProps {
  notification: Notification;
  onDismiss: (id: string) => void;
  onDelete: (id: string) => void;
  /** Currently open card ID — for single-card coordination */
  openCardId: string | null;
  /** Callback when this card starts swiping */
  onSwipeStart: (id: string) => void;
  /** Whether swipe behavior is enabled (mobile only) */
  swipeEnabled: boolean;
  children: React.ReactNode;
}

export function SwipeableNotificationCard({
  notification,
  onDismiss,
  onDelete,
  openCardId,
  onSwipeStart,
  swipeEnabled,
  children,
}: SwipeableNotificationCardProps) {
  const { containerRef, offset, isSwiping } = useSwipeToDismiss({
    actionPanelWidth: 140,
    threshold: 60,
    enabled: swipeEnabled,
    onSwipeStart: () => onSwipeStart(notification.id),
    forceClose: openCardId !== null && openCardId !== notification.id,
  });

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
  };

  const actionPanelStyle: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '140px',
    display: 'flex',
  };

  const dismissButtonStyle: React.CSSProperties = {
    flex: 1,
    backgroundColor: '#757575',
    color: '#fff',
    border: 'none',
    fontSize: '0.85rem',
    cursor: 'pointer',
  };

  const deleteButtonStyle: React.CSSProperties = {
    flex: 1,
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    fontSize: '0.85rem',
    cursor: 'pointer',
  };

  const cardContentStyle: React.CSSProperties = {
    transform: `translateX(-${offset}px)`,
    transition: isSwiping ? 'none' : 'transform 250ms ease-out',
  };

  return (
    <div ref={containerRef} style={containerStyle}>
      <div
        role="group"
        aria-label="Notification actions"
        style={actionPanelStyle}
      >
        <button
          style={dismissButtonStyle}
          aria-label={`Dismiss notification: ${notification.title}`}
          onClick={() => onDismiss(notification.id)}
        >
          Dismiss
        </button>
        <button
          style={deleteButtonStyle}
          aria-label={`Delete notification: ${notification.title}`}
          onClick={() => onDelete(notification.id)}
        >
          Delete
        </button>
      </div>
      <div style={cardContentStyle}>
        {children}
      </div>
    </div>
  );
}
